import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, FileText, Home, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { createPageInput, RESERVED_PAGE_SLUGS } from '@barbeiropro/contracts';
import type { z } from 'zod';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorMessage, HttpError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { pagesKey, type PageSummary } from './types';

const slugify = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

type Values = z.input<typeof createPageInput>;

function PageFormModal({ open, onOpenChange, page, onSaved }: { open: boolean; onOpenChange: (v: boolean) => void; page: PageSummary | null; onSaved: (p: PageSummary) => void }) {
  const qc = useQueryClient();
  const isHome = page?.slug === '';
  const form = useForm<Values, unknown, z.output<typeof createPageInput>>({
    resolver: zodResolver(createPageInput),
    defaultValues: { title: '', slug: '', showInNav: true },
  });
  const { errors, isSubmitting, dirtyFields } = form.formState;

  useEffect(() => {
    if (open) form.reset(page ? { title: page.title, slug: page.slug, showInNav: page.showInNav } : { title: '', slug: '', showInNav: true });
  }, [open, page, form]);

  async function submit(v: z.output<typeof createPageInput>) {
    try {
      const saved = page ? await api.patch<PageSummary>(`/site/pages/${page.id}`, v) : await api.post<PageSummary>('/site/pages', v);
      await qc.invalidateQueries({ queryKey: pagesKey });
      toast.success(page ? 'Página atualizada' : 'Página criada');
      onOpenChange(false);
      onSaved(saved);
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) form.setError('slug', { message: 'Já existe uma página com este endereço.' });
      else if (e instanceof HttpError && e.code === 'reserved_slug') form.setError('slug', { message: 'Este endereço é reservado. Escolha outro.' });
      else toast.error(errorMessage(e));
    }
  }

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={page ? 'Dados da página' : 'Nova página'}
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" form="page-form" disabled={isSubmitting}>{page ? 'Salvar página' : 'Criar página'}</Button>
        </>
      }
    >
      <form id="page-form" onSubmit={form.handleSubmit(submit)} className="grid gap-4" noValidate>
        <Field id="pg-title" label="Nome da página" error={errors.title && 'Informe o nome (até 120 caracteres).'}>
          <Input
            id="pg-title"
            maxLength={120}
            {...form.register('title', {
              onChange: (e) => {
                if (!page && !dirtyFields.slug && !isHome) form.setValue('slug', slugify(e.target.value));
              },
            })}
          />
        </Field>
        <Field
          id="pg-slug"
          label="Endereço"
          hint={isHome ? 'A página inicial usa o endereço principal e não pode mudar.' : `/b/…/${form.watch('slug') || 'endereco'}  ·  letras minúsculas, números e hífens (mínimo 3)`}
          error={errors.slug && (RESERVED_PAGE_SLUGS.includes(form.getValues('slug')) ? 'Endereço reservado.' : (errors.slug.message ?? 'Use letras minúsculas, números e hífens (mínimo 3).'))}
        >
          <Input id="pg-slug" disabled={isHome} maxLength={60} aria-invalid={!!errors.slug} {...form.register('slug')} />
        </Field>
        <label className="flex items-center gap-2.5 text-sm">
          <input type="checkbox" className="size-4 accent-[var(--bottle)]" {...form.register('showInNav')} />
          Mostrar no menu do site
        </label>
      </form>
    </FormModal>
  );
}

export function PagesPanel({ pages, selectedId, onSelect }: { pages: PageSummary[]; selectedId: string | null; onSelect: (id: string) => void }) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PageSummary | null>(null);

  const reorder = useMutation({
    mutationFn: async (next: PageSummary[]) => {
      await Promise.all(next.map((p, i) => (p.position === i ? null : api.patch(`/site/pages/${p.id}`, { position: i }))));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: pagesKey }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const move = (i: number, d: number) => {
    const next = [...pages];
    [next[i], next[i + d]] = [next[i + d]!, next[i]!];
    reorder.mutate(next);
  };

  async function remove(p: PageSummary) {
    try {
      await api.del(`/site/pages/${p.id}`);
      toast.success('Página excluída');
      await qc.invalidateQueries({ queryKey: pagesKey });
      const home = pages.find((x) => x.slug === '');
      if (selectedId === p.id && home) onSelect(home.id);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <nav aria-label="Páginas do site" className="grid content-start gap-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Páginas</h2>
        <Button type="button" size="sm" variant="outline" onClick={() => setCreating(true)}>
          <Plus aria-hidden /> Nova
        </Button>
      </div>
      <ul className="grid gap-1.5">
        {pages.map((p, i) => (
          <li key={p.id} className={cn('rounded-md border bg-card', p.id === selectedId && 'border-primary ring-1 ring-primary')}>
            <button type="button" onClick={() => onSelect(p.id)} aria-current={p.id === selectedId ? 'page' : undefined} className="flex w-full items-center gap-2 px-2.5 py-2 text-left">
              {p.slug === '' ? <Home className="size-4 shrink-0 text-muted-foreground" aria-hidden /> : <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{p.title}</span>
                <span className="block truncate text-xs text-muted-foreground">/{p.slug}{!p.showInNav && ' · fora do menu'}</span>
              </span>
              <Badge variant={p.published ? 'secondary' : 'outline'} className="shrink-0">{p.published ? `No ar v${p.publishedVersionNumber ?? ''}` : 'Rascunho'}</Badge>
            </button>
            <div className="flex items-center gap-0.5 border-t px-1 py-0.5">
              <Button type="button" variant="ghost" size="icon-xs" aria-label={`Subir ${p.title} no menu`} disabled={i === 0 || reorder.isPending} onClick={() => move(i, -1)}><ArrowUp /></Button>
              <Button type="button" variant="ghost" size="icon-xs" aria-label={`Descer ${p.title} no menu`} disabled={i === pages.length - 1 || reorder.isPending} onClick={() => move(i, 1)}><ArrowDown /></Button>
              <Button type="button" variant="ghost" size="icon-xs" aria-label={`Editar dados de ${p.title}`} onClick={() => setEditing(p)}><Pencil /></Button>
              {p.slug !== '' && (
                <ConfirmDialog
                  trigger={<Button type="button" variant="ghost" size="icon-xs" className="ml-auto text-destructive" aria-label={`Excluir ${p.title}`}><Trash2 /></Button>}
                  title={`Excluir a página "${p.title}"?`}
                  description="Ela sai do ar e do menu do site. Quem tiver o link verá página não encontrada."
                  confirmLabel="Excluir página"
                  onConfirm={() => remove(p)}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
      <PageFormModal open={creating} onOpenChange={setCreating} page={null} onSaved={(p) => onSelect(p.id)} />
      <PageFormModal open={editing !== null} onOpenChange={(v) => !v && setEditing(null)} page={editing} onSaved={() => undefined} />
    </nav>
  );
}
